/**
 * Scenario Scene - Cinematic decision simulation
 *
 * Design principles:
 * - Each decision is a cinematic moment
 * - Visual noise reduced, attention tunneled
 * - Motion communicates risk and intent
 * - Premium, government-fundable experience
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { BaseScene, type SceneContext } from './BaseScene';
import { Button, DecisionCard, Panel, ScreenGlow, CinematicOverlay, ProgressIndicator } from '@ui/index';
import { createUserMarker, createIcon } from '@ui/Icons';
import { theme } from '@core/theme';
import { app } from '@core/Application';
import { stateMachine } from '@core/StateMachine';
import { eventBus } from '@core/EventBus';
import type { Scenario, StateNode, DecisionPoint, FeedbackContent } from '@data/types';
import { getScenariosForPhase } from '@data/scenarios';

type ScenarioState = 'loading' | 'presenting' | 'decision' | 'feedback' | 'complete';

// Layout constants - tighter framing for drama
const UI_PANEL_HEIGHT = 200;
const SCENE_PADDING = 24;
const CAMERA_ZOOM = 1.15; // 15% zoom for closer framing

export class ScenarioScene extends BaseScene {
  private scenarioState: ScenarioState = 'loading';
  private currentNode: StateNode | null = null;

  // Layers
  private sceneLayer!: Container;
  private environmentLayer!: Container;  // Background, buildings (parallax slow)
  private roadLayer!: Container;          // Road, markings (parallax medium)
  private actorLayer!: Container;         // Pedestrian, vehicles (no parallax)
  private uiPanelLayer!: Container;
  private overlayLayer!: Container;
  private screenGlow!: ScreenGlow;
  private cinematicOverlay!: CinematicOverlay;

  // Scene elements
  private buildings: Graphics[] = [];
  private roadMarkings: Graphics[] = [];

  // Moving vehicles
  private movingCars: { container: Container; speed: number; lane: number }[] = [];
  private carAnimationFrame: number | null = null;

  // UI elements
  private decisionCards: DecisionCard[] = [];
  private feedbackPanel!: Panel;
  private progressIndicator!: ProgressIndicator;
  private uiPanelBg!: Graphics;
  private promptContainer!: Container;

  // State
  private scenarioIndex = 0;
  private phaseScenarios: Scenario[] = [];
  private sceneHeight = 0;

  constructor(context: SceneContext) {
    super(context);
  }

  async init(): Promise<void> {
    this.sceneHeight = this.context.height - UI_PANEL_HEIGHT;

    // Create layer hierarchy
    this.sceneLayer = new Container();
    this.environmentLayer = new Container();
    this.roadLayer = new Container();
    this.actorLayer = new Container();
    this.uiPanelLayer = new Container();
    this.overlayLayer = new Container();

    // Build scene layer structure
    this.sceneLayer.addChild(this.environmentLayer);
    this.sceneLayer.addChild(this.roadLayer);
    this.sceneLayer.addChild(this.actorLayer);

    // Apply zoom for tighter framing
    this.sceneLayer.scale.set(CAMERA_ZOOM);
    this.sceneLayer.position.set(
      (this.context.width - this.context.width * CAMERA_ZOOM) / 2,
      (this.sceneHeight - this.sceneHeight * CAMERA_ZOOM) / 2
    );

    this.addChild(this.sceneLayer);
    this.addChild(this.uiPanelLayer);
    this.addChild(this.overlayLayer);

    // Cinematic overlay (vignette + dimming)
    this.cinematicOverlay = new CinematicOverlay({
      width: this.context.width,
      height: this.sceneHeight,
    });
    this.addChild(this.cinematicOverlay);

    // Screen glow for consequence feedback
    this.screenGlow = new ScreenGlow(this.context.width, this.context.height);
    this.addChild(this.screenGlow);

    // Draw scene
    this.drawSceneBackground();
    this.drawUIPanel();

    // Progress indicator
    const session = stateMachine.getSessionState();
    const role = session.selectedRole || 'pedestrian';
    this.phaseScenarios = getScenariosForPhase(session.currentPhase, role);

    // Ensure scenarioIndex is within bounds
    this.scenarioIndex = Math.min(
      session.currentScenarioIndex,
      Math.max(0, this.phaseScenarios.length - 1)
    );

    this.progressIndicator = new ProgressIndicator({
      totalScenarios: this.phaseScenarios.length,
      currentScenario: this.scenarioIndex,
      phase: session.currentPhase,
    });
    this.progressIndicator.position.set(SCENE_PADDING, SCENE_PADDING);
    this.actorLayer.addChild(this.progressIndicator);

    // Start animation loop
    this.startAnimationLoop();

    // Start first scenario
    if (this.phaseScenarios.length > 0 && this.phaseScenarios[this.scenarioIndex]) {
      await this.loadScenario(this.phaseScenarios[this.scenarioIndex]);
    } else {
      this.showNoScenariosMessage();
    }

    this.setupKeyboard();
  }

  /**
   * Draw the environment layer (sky, buildings - parallax slow)
   */
  private drawSceneBackground(): void {
    // Clear existing
    this.environmentLayer.removeChildren();
    this.roadLayer.removeChildren();
    this.buildings = [];
    this.roadMarkings = [];

    // Sky gradient
    const sky = new Graphics();
    sky.rect(0, 0, this.context.width, this.sceneHeight);
    sky.fill(theme.colors.sky);
    this.environmentLayer.addChild(sky);

    // Horizon line (subtle)
    const horizonY = this.sceneHeight * 0.38;
    const horizon = new Graphics();
    horizon.rect(0, horizonY - 1, this.context.width, 2);
    horizon.fill({ color: 0x4a5a6a, alpha: 0.3 });
    this.environmentLayer.addChild(horizon);

    // Buildings silhouette (parallax animated)
    const buildingY = this.sceneHeight * 0.38;
    for (let i = 0; i < 10; i++) {
      const building = new Graphics();
      const x = i * (this.context.width / 8) - this.context.width * 0.1;
      const w = (this.context.width / 8) - 6;
      const h = 35 + Math.random() * 55;

      building.rect(x, buildingY - h, w, h);
      building.fill({ color: theme.colors.neutralDark, alpha: 0.4 });

      // Windows (subtle detail)
      const windowRows = Math.floor(h / 12);
      const windowCols = Math.floor(w / 10);
      for (let row = 0; row < windowRows; row++) {
        for (let col = 0; col < windowCols; col++) {
          if (Math.random() > 0.4) {
            building.rect(
              x + 4 + col * 10,
              buildingY - h + 8 + row * 12,
              6, 8
            );
            building.fill({ color: 0x5a6a7a, alpha: 0.3 });
          }
        }
      }

      this.buildings.push(building);
      this.environmentLayer.addChild(building);
    }

    // Sidewalk
    const sidewalkY = this.sceneHeight * 0.48;
    const sidewalk = new Graphics();
    sidewalk.rect(0, sidewalkY, this.context.width, 55);
    sidewalk.fill(theme.colors.sidewalk);

    // Sidewalk edge line
    sidewalk.rect(0, sidewalkY + 53, this.context.width, 2);
    sidewalk.fill({ color: theme.colors.neutralDark, alpha: 0.5 });
    this.roadLayer.addChild(sidewalk);

    // Road surface
    const roadY = sidewalkY + 55;
    const road = new Graphics();
    road.rect(0, roadY, this.context.width, this.sceneHeight - roadY + 50);
    road.fill(theme.colors.road);
    this.roadLayer.addChild(road);

    // Road center line (dashed - parallax animated)
    const centerY = roadY + (this.sceneHeight - roadY) / 2 + 10;
    const markingWidth = 45;
    const markingGap = 30;
    let x = -markingGap;
    while (x < this.context.width + markingWidth) {
      const marking = new Graphics();
      marking.rect(x, centerY - 2, markingWidth, 4);
      marking.fill({ color: theme.colors.roadMarking, alpha: 0.7 });
      this.roadMarkings.push(marking);
      this.roadLayer.addChild(marking);
      x += markingWidth + markingGap;
    }

    // Crosswalk - centered, prominent
    const crosswalkX = this.context.width / 2 - 55;
    const stripeWidth = 10;
    const stripeGap = 12;
    for (let i = 0; i < 6; i++) {
      const stripe = new Graphics();
      stripe.rect(
        crosswalkX + i * (stripeWidth + stripeGap),
        roadY,
        stripeWidth,
        this.sceneHeight - roadY + 50
      );
      stripe.fill({ color: 0xffffff, alpha: 0.75 });
      this.roadLayer.addChild(stripe);
    }

    // Road edge line
    const edgeLine = new Graphics();
    edgeLine.rect(0, roadY, this.context.width, 3);
    edgeLine.fill({ color: theme.colors.roadMarking, alpha: 0.6 });
    this.roadLayer.addChild(edgeLine);
  }

  /**
   * Draw the bottom UI panel
   */
  private drawUIPanel(): void {
    // Remove existing
    const existing = this.uiPanelLayer.children.filter(c => c.label === 'uiPanelBg');
    existing.forEach(c => this.uiPanelLayer.removeChild(c));

    // Background panel with subtle gradient effect
    this.uiPanelBg = new Graphics();
    this.uiPanelBg.label = 'uiPanelBg';

    // Main background
    this.uiPanelBg.rect(0, this.sceneHeight, this.context.width, UI_PANEL_HEIGHT);
    this.uiPanelBg.fill(theme.colors.panelBg);

    // Top accent line
    this.uiPanelBg.rect(0, this.sceneHeight, this.context.width, 1);
    this.uiPanelBg.fill({ color: theme.colors.neutral, alpha: 0.2 });

    // Subtle top gradient fade
    for (let i = 0; i < 8; i++) {
      this.uiPanelBg.rect(0, this.sceneHeight + 1 + i, this.context.width, 1);
      this.uiPanelBg.fill({ color: 0x000000, alpha: 0.03 * (8 - i) });
    }

    this.uiPanelLayer.addChild(this.uiPanelBg);
  }

  /**
   * Start car animation loop
   */
  private startAnimationLoop(): void {
    // Create initial moving cars
    this.spawnMovingCars();

    // Simple animation loop for cars only
    const animateCars = () => {
      this.movingCars.forEach((car) => {
        car.container.position.x += car.speed;

        // Reset car when it goes off screen
        if (car.speed > 0 && car.container.position.x > this.context.width + 100) {
          car.container.position.x = -80;
        } else if (car.speed < 0 && car.container.position.x < -100) {
          car.container.position.x = this.context.width + 80;
        }
      });

      this.carAnimationFrame = requestAnimationFrame(animateCars);
    };

    this.carAnimationFrame = requestAnimationFrame(animateCars);
  }

  /**
   * Spawn moving background cars
   */
  private spawnMovingCars(): void {
    // Clear existing
    this.movingCars.forEach((car) => car.container.destroy());
    this.movingCars = [];

    const roadY = this.sceneHeight * 0.48 + 55;
    const laneHeight = (this.sceneHeight - roadY) / 2;

    // Car going right (far lane)
    const car1 = this.createMovingCar(0x4a5568);
    car1.position.set(-50, roadY + laneHeight * 1.5);
    car1.scale.set(0.8);
    this.roadLayer.addChild(car1);
    this.movingCars.push({ container: car1, speed: 1.5, lane: 1 });

    // Car going left (near lane) - slightly faster
    const car2 = this.createMovingCar(0x718096);
    car2.position.set(this.context.width + 50, roadY + laneHeight * 0.6);
    car2.scale.set(-0.9, 0.9); // Flip horizontally
    this.roadLayer.addChild(car2);
    this.movingCars.push({ container: car2, speed: -2, lane: 0 });

    // Third car, slower
    const car3 = this.createMovingCar(0x2d3748);
    car3.position.set(this.context.width * 0.3, roadY + laneHeight * 1.5);
    car3.scale.set(0.7);
    this.roadLayer.addChild(car3);
    this.movingCars.push({ container: car3, speed: 1.2, lane: 1 });
  }

  /**
   * Create a simple car graphic
   */
  private createMovingCar(color: number): Container {
    const car = new Container();
    const g = new Graphics();

    // Car body
    g.roundRect(0, 0, 60, 24, 4);
    g.fill(color);

    // Roof
    g.roundRect(12, -10, 28, 12, 3);
    g.fill(color);

    // Windows
    g.roundRect(14, -8, 10, 8, 2);
    g.fill({ color: 0x87ceeb, alpha: 0.6 });
    g.roundRect(28, -8, 10, 8, 2);
    g.fill({ color: 0x87ceeb, alpha: 0.6 });

    // Wheels
    g.circle(12, 24, 6);
    g.fill(0x1a202c);
    g.circle(48, 24, 6);
    g.fill(0x1a202c);

    car.addChild(g);
    return car;
  }

  private async loadScenario(scenario: Scenario): Promise<void> {
    this.scenarioState = 'loading';
    this.currentNode = stateMachine.loadScenario(scenario);

    this.clearDecisionUI();
    this.clearFeedbackUI();

    await this.presentNode(this.currentNode);
  }

  private async presentNode(node: StateNode): Promise<void> {
    this.currentNode = node;
    this.scenarioState = 'presenting';

    this.renderSceneConfig(node.sceneConfig);

    if (node.feedback) {
      await this.showFeedback(node.feedback);
    }

    if (node.isTerminal) {
      this.scenarioState = 'complete';
      await this.showScenarioComplete();
      return;
    }

    if (node.decisionPoint) {
      await this.showDecisionPoint(node.decisionPoint);
    }
  }

  private renderSceneConfig(_config: StateNode['sceneConfig']): void {
    // Remove existing actors
    const existingActors = this.actorLayer.children.filter(c => c.label === 'actor');
    existingActors.forEach(a => this.actorLayer.removeChild(a));

    // Conflict zone center (for spotlight)
    const conflictX = this.context.width / 2;
    const conflictY = this.sceneHeight * 0.55;

    // Get current role from session
    const session = stateMachine.getSessionState();
    const currentRole = session.selectedRole || 'pedestrian';

    // User position marker - role-specific icon
    const userMarker = createUserMarker(currentRole, 48);
    userMarker.label = 'actor';
    userMarker.position.set(conflictX, this.sceneHeight * 0.50);
    this.actorLayer.addChild(userMarker);

    // "You" label
    const youLabel = new Text({
      text: 'YOU',
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.xs,
        fontWeight: theme.typography.weights.bold,
        fill: theme.colors.textPrimary,
        letterSpacing: 2,
      }),
    });
    youLabel.label = 'actor';
    youLabel.anchor.set(0.5, 0);
    youLabel.position.set(conflictX, this.sceneHeight * 0.50 + 30);
    this.actorLayer.addChild(youLabel);

    // Traffic light
    const trafficLight = createIcon('traffic-light', 44, theme.colors.textSecondary);
    trafficLight.label = 'actor';
    trafficLight.position.set(conflictX + 90, this.sceneHeight * 0.42);
    this.actorLayer.addChild(trafficLight);

    // Update cinematic spotlight position
    this.cinematicOverlay.setSpotlight(conflictX, conflictY, 140);
  }

  private async showDecisionPoint(decision: DecisionPoint): Promise<void> {
    this.scenarioState = 'decision';
    this.clearDecisionUI();

    // Enter cinematic decision mode
    await this.cinematicOverlay.enterDecisionMode(
      this.context.width / 2,
      this.sceneHeight * 0.55
    );

    // Create prompt container
    this.promptContainer = new Container();
    this.promptContainer.label = 'decisionUI';
    this.promptContainer.position.set(0, this.sceneHeight + 12);
    this.uiPanelLayer.addChild(this.promptContainer);

    // Situation text - bold, prominent
    const situationText = new Text({
      text: this.extractSituation(decision.prompt),
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.lg,
        fontWeight: theme.typography.weights.semibold,
        fill: theme.colors.textPrimary,
        wordWrap: true,
        wordWrapWidth: this.context.width - 48,
        lineHeight: theme.typography.sizes.lg * theme.typography.lineHeight.normal,
      }),
    });
    situationText.position.set(SCENE_PADDING, 0);
    this.promptContainer.addChild(situationText);

    // Risk cue text - lighter, secondary
    const riskCue = this.extractRiskCue(decision.prompt);
    if (riskCue) {
      const riskText = new Text({
        text: riskCue,
        style: new TextStyle({
          fontFamily: theme.typography.fontFamily,
          fontSize: theme.typography.sizes.sm,
          fill: theme.colors.textSecondary,
          wordWrap: true,
          wordWrapWidth: this.context.width - 48,
          lineHeight: theme.typography.sizes.sm * theme.typography.lineHeight.relaxed,
        }),
      });
      riskText.position.set(SCENE_PADDING, situationText.height + 4);
      this.promptContainer.addChild(riskText);
    }

    // Decision cards - staggered entrance
    const cardWidth = 280;
    const cardGap = 20;
    const numCards = decision.options.length;
    const totalWidth = numCards * cardWidth + (numCards - 1) * cardGap;
    const startX = (this.context.width - totalWidth) / 2;
    const cardsY = this.sceneHeight + 65;

    decision.options.forEach((option, index) => {
      const card = new DecisionCard({
        option,
        width: cardWidth,
        index,
      });

      const x = startX + index * (cardWidth + cardGap);
      card.position.set(x, cardsY);
      card.label = 'decisionUI';
      card.setOnSelect((optionId) => this.handleDecision(optionId));

      this.uiPanelLayer.addChild(card);
      this.decisionCards.push(card);

      // Staggered entrance animation
      card.animateEntrance(index * theme.decisionMoment.cardStaggerDelay);
    });

    stateMachine.startDecisionTimer();
  }

  /**
   * Extract the main situation statement from prompt
   */
  private extractSituation(prompt: string): string {
    // Take the first sentence as the situation
    const firstSentence = prompt.split('.')[0];
    return firstSentence + '.';
  }

  /**
   * Extract secondary risk cue from prompt
   */
  private extractRiskCue(prompt: string): string | null {
    const sentences = prompt.split('.');
    if (sentences.length > 1) {
      return sentences.slice(1).join('.').trim();
    }
    return null;
  }

  private async handleDecision(decisionId: string): Promise<void> {
    if (this.scenarioState !== 'decision') return;

    // Disable other cards immediately
    this.decisionCards.forEach((card) => {
      if (card.getOptionId() !== decisionId) {
        card.setDisabled(true);
      }
    });

    // Exit cinematic mode
    await this.cinematicOverlay.exitDecisionMode();

    const result = stateMachine.makeDecision(decisionId);

    if (result) {
      // Brief pause before showing feedback
      await new Promise(resolve => setTimeout(resolve, 200));

      await this.showFeedback(result.edge.immediateFeedback);
      this.presentNode(result.nextNode);
    }
  }

  private async showFeedback(feedback: FeedbackContent): Promise<void> {
    this.scenarioState = 'feedback';
    this.clearDecisionUI();

    // Cinematic consequence flash (skip neutral for dramatic effect)
    if (feedback.severity !== 'neutral') {
      await this.cinematicOverlay.flashConsequence(feedback.severity);
    }

    // Show screen glow based on severity
    if (feedback.severity === 'positive') {
      this.screenGlow.show('positive');
    } else if (feedback.severity === 'warning') {
      this.screenGlow.pulse(2);
    } else if (feedback.severity === 'negative') {
      this.screenGlow.show('negative');
    }

    return new Promise((resolve) => {
      // Create feedback panel
      this.feedbackPanel = new Panel({
        width: Math.min(480, this.context.width - 48),
        severity: feedback.severity,
      });
      this.feedbackPanel.addTitle(feedback.title);
      this.feedbackPanel.addText(feedback.description);

      if (feedback.principle) {
        this.feedbackPanel.addSpacer(12);
        this.feedbackPanel.addPrinciple(feedback.principle);
      }

      const panelDims = this.feedbackPanel.getDimensions();
      this.feedbackPanel.position.set(
        (this.context.width - panelDims.width) / 2,
        (this.sceneHeight - panelDims.height) / 2
      );
      this.feedbackPanel.label = 'feedbackUI';
      this.overlayLayer.addChild(this.feedbackPanel);

      // Backdrop dimmer
      const dimmer = new Graphics();
      dimmer.rect(0, 0, this.context.width, this.sceneHeight);
      dimmer.fill({ color: 0x000000, alpha: 0.5 });
      dimmer.label = 'feedbackUI';
      this.overlayLayer.addChildAt(dimmer, 0);

      // Continue button
      const continueBtn = new Button({
        label: 'Continue',
        width: 140,
        height: 44,
        variant: 'primary',
        size: 'md',
      });
      continueBtn.position.set(
        (this.context.width - 140) / 2,
        this.feedbackPanel.position.y + panelDims.height + 24
      );
      continueBtn.label = 'feedbackUI';
      continueBtn.setOnClick(() => {
        this.clearFeedbackUI();
        this.screenGlow.hide();
        resolve();
      });
      this.overlayLayer.addChild(continueBtn);
    });
  }

  private async showScenarioComplete(): Promise<void> {
    stateMachine.completeScenario();
    this.progressIndicator.update(this.scenarioIndex + 1);

    const panel = new Panel({
      width: Math.min(420, this.context.width - 48),
      severity: 'positive',
    });
    panel.addTitle('Scenario Complete');
    panel.addText('Well done. You have completed this scenario.');

    const panelDims = panel.getDimensions();
    panel.position.set(
      (this.context.width - panelDims.width) / 2,
      (this.sceneHeight - panelDims.height) / 2 - 20
    );
    panel.label = 'feedbackUI';
    this.overlayLayer.addChild(panel);

    // Dimmer
    const dimmer = new Graphics();
    dimmer.rect(0, 0, this.context.width, this.sceneHeight);
    dimmer.fill({ color: 0x000000, alpha: 0.5 });
    dimmer.label = 'feedbackUI';
    this.overlayLayer.addChildAt(dimmer, 0);

    if (this.scenarioIndex + 1 < this.phaseScenarios.length) {
      const nextBtn = new Button({
        label: 'Next Scenario',
        width: 160,
        height: 48,
        variant: 'primary',
      });
      nextBtn.position.set(
        (this.context.width - 160) / 2,
        panel.position.y + panelDims.height + 24
      );
      nextBtn.label = 'feedbackUI';
      nextBtn.setOnClick(() => {
        this.scenarioIndex++;
        this.progressIndicator.update(this.scenarioIndex);
        this.clearFeedbackUI();
        this.loadScenario(this.phaseScenarios[this.scenarioIndex]);
      });
      this.overlayLayer.addChild(nextBtn);
    } else {
      const summaryBtn = new Button({
        label: 'View Summary',
        width: 160,
        height: 48,
        variant: 'primary',
      });
      summaryBtn.position.set(
        (this.context.width - 160) / 2,
        panel.position.y + panelDims.height + 24
      );
      summaryBtn.label = 'feedbackUI';
      summaryBtn.setOnClick(() => {
        eventBus.emit('phase:complete', { phase: stateMachine.getSessionState().currentPhase });
        app.navigateTo('summary');
      });
      this.overlayLayer.addChild(summaryBtn);
    }
  }

  private showNoScenariosMessage(): void {
    const panel = new Panel({
      width: Math.min(420, this.context.width - 48),
    });
    panel.addTitle('Scenarios Coming Soon');
    panel.addText('Scenarios for this phase are being developed.');

    const panelDims = panel.getDimensions();
    panel.position.set(
      (this.context.width - panelDims.width) / 2,
      (this.context.height - panelDims.height) / 2
    );
    this.overlayLayer.addChild(panel);

    const backBtn = new Button({
      label: 'Back to Roles',
      width: 160,
      height: 48,
      variant: 'secondary',
    });
    backBtn.position.set(
      (this.context.width - 160) / 2,
      panel.position.y + panelDims.height + 20
    );
    backBtn.setOnClick(() => app.navigateTo('role-select'));
    this.overlayLayer.addChild(backBtn);
  }

  private clearDecisionUI(): void {
    const toRemove = this.uiPanelLayer.children.filter(c => c.label === 'decisionUI');
    toRemove.forEach(c => this.uiPanelLayer.removeChild(c));
    this.decisionCards = [];
  }

  private clearFeedbackUI(): void {
    const toRemove = this.overlayLayer.children.filter(c => c.label === 'feedbackUI');
    toRemove.forEach(c => {
      c.destroy();
    });
  }

  private setupKeyboard(): void {
    const handleKeydown = (e: KeyboardEvent) => {
      if (!this.isActive || this.scenarioState !== 'decision') return;

      const num = parseInt(e.key);
      if (num >= 1 && num <= this.decisionCards.length) {
        const card = this.decisionCards[num - 1];
        if (card) {
          card.select();
        }
      }
    };

    window.addEventListener('keydown', handleKeydown);
    this.on('destroyed', () => {
      window.removeEventListener('keydown', handleKeydown);
    });
  }

  protected handleResize(): void {
    this.sceneHeight = this.context.height - UI_PANEL_HEIGHT;

    // Update scene layer transform
    this.sceneLayer.scale.set(CAMERA_ZOOM);
    this.sceneLayer.position.set(
      (this.context.width - this.context.width * CAMERA_ZOOM) / 2,
      (this.sceneHeight - this.sceneHeight * CAMERA_ZOOM) / 2
    );

    this.drawSceneBackground();
    this.drawUIPanel();
    this.screenGlow.resize(this.context.width, this.context.height);
    this.cinematicOverlay.resize(this.context.width, this.sceneHeight);
  }

  destroy(): void {
    if (this.carAnimationFrame) {
      cancelAnimationFrame(this.carAnimationFrame);
    }
    this.movingCars.forEach((car) => car.container.destroy());
    this.clearDecisionUI();
    this.clearFeedbackUI();
    super.destroy();
  }
}
